<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import type { MentorshipCapabilities, StarterKitStatus } from '../types'
import { mentorshipApi } from '../services/mentorshipApi'
import { useMentorshipSettingsStore } from '../stores/mentorshipSettingsStore'
import MentorshipDialog from './MentorshipDialog.vue'

const settingsDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const router = useRouter()
const route = useRoute()
const loaded = ref(false)
const settingsReady = ref(false)
const close = () => router.push({ name: 'mentorship-cases', query: route.query })
onBeforeRouteLeave(() => settingsDialogRef.value?.confirmDismiss() ?? true)
const store = useMentorshipSettingsStore()
const saved = ref(false)
const capabilities = ref<MentorshipCapabilities | null>(null)
const settingsForm = ref()
const integerRule = (minimum: number) => (value: unknown) => (Number.isInteger(Number(value)) && Number(value) >= minimum) || `Укажите целое число от ${minimum}`
const starterKit = ref<StarterKitStatus | null>(null)
const starterKitLoading = ref(false)
const starterKitError = ref<string | null>(null)

async function save() {
  saved.value = false
  if (!(await settingsForm.value?.validate())?.valid) return
  try {
    await store.saveSettings()
    await nextTick()
    settingsDialogRef.value?.markSaved()
    saved.value = true
  } catch { /* Store exposes the API error in the form. */ }
}

async function installStarterKit() {
  starterKitLoading.value = true
  starterKitError.value = null
  try {
    starterKit.value = await mentorshipApi.installStarterKit()
    await store.fetchCatalogs()
    store.rules = await mentorshipApi.getRules()
  } catch {
    starterKitError.value = 'Не удалось загрузить стартовый комплект'
  } finally {
    starterKitLoading.value = false
  }
}

async function load() {
  loaded.value = false
  starterKitError.value = null
  try {
    capabilities.value = await mentorshipApi.getCapabilities()
    if (!capabilities.value.can_manage_settings) return
    await Promise.all([store.fetchAll().then(() => {
      settingsReady.value = !store.error
    }), mentorshipApi.getStarterKitStatus().then(value => {
      starterKit.value = value
    })])
  } catch {
    starterKitError.value = 'Не удалось загрузить настройки. Повторите загрузку.'
  } finally {
    loaded.value = true
  }
}
onMounted(load)
</script>

<template>
  <MentorshipDialog
    ref="settingsDialogRef"
    model-value
    title="Настройки наставничества"
    embedded
    :busy="store.saving || starterKitLoading"
    :loading="!loaded"
    :draft="loaded && settingsReady ? store.settings : null"
    @update:model-value="close"
  >
    <VCardText class="entity-form-scroll-body">
      <VAlert v-if="capabilities && !capabilities.can_manage_settings" type="info" variant="tonal">
        Настройки доступны пользователю с правом управления наставничеством.
      </VAlert>
      <VForm
        v-if="capabilities?.can_manage_settings"
        id="mentorship-settings-form"
        ref="settingsForm"
        :disabled="store.loading || store.saving || !settingsReady"
        @submit.prevent="save"
      >
        <VAlert
          v-if="store.error"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ store.error }}
          <VBtn
            v-if="!settingsReady"
            variant="text"
            :disabled="store.loading"
            @click="load"
          >
            Повторить загрузку
          </VBtn>
        </VAlert>
        <VAlert
          v-if="saved"
          type="success"
          variant="tonal"
          class="mb-4"
        >
          Настройки сохранены
        </VAlert>
        <VCard :loading="store.loading" class="border" elevation="0">
          <VCardTitle>Общие правила</VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12" md="4">
                <VTextField
                  v-model.number="store.settings.max_active_mentees_per_mentor"
                  type="number"
                  :rules="[integerRule(1)]"
                  min="1"
                  label="Лимит на наставника"
                  hint="Максимум одновременно активных сотрудников."
                  persistent-hint
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model.number="store.settings.activity_reminder_days"
                  type="number"
                  :rules="[integerRule(0)]"
                  min="0"
                  label="Напоминать за, дней"
                  hint="До срока мероприятия."
                  persistent-hint
                />
              </VCol>
              <VCol cols="12" md="4">
                <VTextField
                  v-model.number="store.settings.inactivity_risk_days"
                  type="number"
                  :rules="[integerRule(1)]"
                  min="1"
                  label="Без активности, дней"
                  hint="Когда показывать сигнал об отсутствии активности."
                  persistent-hint
                />
              </VCol>
              <VCol cols="12">
                <VSwitch v-model="store.settings.require_final_documents" label="Проверять комплект документов перед завершением" color="primary" />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
        <VCard class="border mt-4" elevation="0">
          <VCardTitle>Стартовый комплект</VCardTitle>
          <VCardText>
            <VAlert
              v-if="starterKitError"
              type="error"
              variant="tonal"
              class="mb-3"
            >
              {{ starterKitError }}
            </VAlert>
            <p class="mb-3">
              Готовые компетенции, программы, материалы, тест из 80 вопросов, шаблоны документов и связи с нормативной базой.
              Существующие данные организации не перезаписываются.
            </p>
            <div class="d-flex flex-wrap align-center gap-3">
              <VChip :color="starterKit?.installed ? 'success' : 'secondary'">
                {{ starterKit?.installed ? `Установлена версия ${starterKit.version}` : 'Не установлен' }}
              </VChip>
              <VBtn
                variant="outlined"
                :loading="starterKitLoading"
                @click="installStarterKit"
              >
                {{ starterKit?.installed ? 'Проверить и дополнить' : 'Загрузить комплект' }}
              </VBtn>
            </div>
            <VAlert
              v-if="starterKit?.report?.warnings.length"
              type="warning"
              variant="tonal"
              class="mt-3"
            >
              Не найдены некоторые нормативные связи: {{ starterKit.report.warnings.length }}. Остальные данные загружены.
            </VAlert>
          </VCardText>
        </VCard>
        <VCard class="border mt-4" elevation="0">
          <VCardTitle>Правила организации</VCardTitle>
          <VCardText class="pb-0">
            Эти правила помогают подобрать программу адаптации. Применимость закона и срок обязательного наставничества ответственный проверяет отдельно.
          </VCardText>
          <VDataTable
            :items="store.rules"
            :headers="[
              { title: 'Название', key: 'name' },
              { title: 'Версия', key: 'version' },
              { title: 'Действует с', key: 'effective_from' },
              { title: 'Срок, мес.', key: 'duration_months' },
              { title: 'Активно', key: 'is_active' },
            ]"
            :items-per-page="10"
          >
            <template #item.is_active="{ item }">
              <VChip :color="item.is_active ? 'success' : 'secondary'" size="small">
                {{ item.is_active ? 'Да' : 'Нет' }}
              </VChip>
            </template>
            <template #no-data>
              <div class="text-center text-italic py-4">
                Данные отсутствуют
              </div>
            </template>
          </VDataTable>
        </VCard>
      </VForm>
      <VAlert v-if="!capabilities && starterKitError" type="error" variant="tonal">
        {{ starterKitError }} <VBtn variant="text" @click="load">
          Повторить
        </VBtn>
      </VAlert>
    </VCardText>
    <VCardActions v-if="capabilities?.can_manage_settings">
      <VBtn
        variant="outlined"
        color="secondary"
        :disabled="store.saving || starterKitLoading"
        @click="settingsDialogRef?.close()"
      >
        Закрыть
      </VBtn>
      <VBtn
        class="general-button"
        variant="elevated"
        :loading="store.saving"
        :disabled="store.loading || starterKitLoading || !settingsReady"
        prepend-icon="ri-save-line"
        type="submit"
        form="mentorship-settings-form"
      >
        Сохранить
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
</template>
